import Box from "@mui/material/Box";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import { Typography } from "./Typography";
import type { StatusPanelProps } from "../types";

/**
 * Containment note (types.ts): no upstream generic is involved — composed
 * from `Box`.
 *
 * Mirrors the prototype's `PortalPanel`: a bordered, tinted panel holding
 * one or more paragraphs, used for AC-18's "this section is with a
 * colleague" help text and AC-17's post-assign/reassign/take-back status
 * message above the section list. Each entry in `paragraphs` renders as
 * its own `Typography variant="body"` — never one string with embedded
 * line breaks — so a screen reader announces each as a separate block.
 */
export function StatusPanel({ paragraphs, tone = "info", id, testId, h2Panel, spaced = true }: Readonly<StatusPanelProps>) {
  if (h2Panel === true) {
    // H2's PortalPanel: the section head's notices (spaced) and the section list's notice.
    return (
      <Box
        {...cleanProps({ id, "data-testid": testId })}
        sx={{
          marginTop: spaced ? `${tokens.spacing.xs}px` : 0,
          padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
          backgroundColor: tokens.colour.neutral.surface,
          borderLeft: `4px solid ${tokens.colour.brand[500]}`,
          fontSize: 15,
          lineHeight: 1.5,
          color: tokens.colour.neutral.ink,
        }}
      >
        {paragraphs.join(" ")}
      </Box>
    );
  }
  return (
    <Box
      {...cleanProps({ id, "data-testid": testId })}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${tokens.spacing.sm}px`,
        padding: `${tokens.spacing.md}px`,
        borderRadius: `${tokens.interactionState.radius.control}px`,
        border: `1px solid ${tone === "info" ? tokens.colour.brand[100] : tokens.colour.neutral.borderSubtle}`,
        backgroundColor: tone === "info" ? tokens.colour.accent[50] : tokens.colour.neutral.surface,
      }}
    >
      {paragraphs.map((paragraph) => (
        <Typography key={paragraph} variant="body">
          {paragraph}
        </Typography>
      ))}
    </Box>
  );
}
